import { findSupportedContentLanguage } from '@/utils/contentLanguages';

const SPEAKING_UNITS_PER_MINUTE: Record<string, number> = {
  zh: 260,
  ja: 280,
  ko: 250,
  en: 180,
  es: 160,
  fr: 160,
  de: 150,
  pt: 170,
  it: 170,
  ru: 160,
  id: 170,
};

const CHARACTER_BASED_LANGUAGES = new Set(['zh', 'ja', 'ko']);
export const FACELESS_VISUAL_PACE_OPTIONS = [5, 6, 8, 10] as const;
export const DEFAULT_FACELESS_VISUAL_PACE_SECONDS = 8;
export type FacelessVisualPaceOption =
  typeof FACELESS_VISUAL_PACE_OPTIONS[number];

export type ResolvedFacelessVisualPaceOption = {
  seconds: number;
  isLegacy: boolean;
};

export function isFacelessVisualPaceOption(
  value: unknown,
): value is FacelessVisualPaceOption {
  const requestedPace = Number(value);
  return Number.isInteger(requestedPace) &&
    FACELESS_VISUAL_PACE_OPTIONS.includes(
      requestedPace as FacelessVisualPaceOption,
    );
}

export function normalizeFacelessVisualPace(value: unknown): number {
  const requestedPace = Math.round(Number(value));
  return Number.isFinite(requestedPace) &&
    requestedPace >= 4 &&
    requestedPace <= 10
    ? requestedPace
    : DEFAULT_FACELESS_VISUAL_PACE_SECONDS;
}

export function resolveFacelessVisualPaceOptions(
  currentValue: unknown,
): ResolvedFacelessVisualPaceOption[] {
  const currentPace = Number(currentValue);
  const standardOptions = FACELESS_VISUAL_PACE_OPTIONS.map((seconds) => ({
    seconds,
    isLegacy: false,
  }));

  if (
    Number.isInteger(currentPace) &&
    currentPace >= 4 &&
    currentPace <= 10 &&
    !isFacelessVisualPaceOption(currentPace)
  ) {
    return [
      { seconds: currentPace, isLegacy: true },
      ...standardOptions,
    ];
  }

  return standardOptions;
}

export function resolveFacelessVisualPaceSubmission(
  requestedValue: unknown,
  existingValue?: unknown,
): number | null {
  const requestedPace = Number(requestedValue);
  if (!Number.isInteger(requestedPace)) return null;
  if (isFacelessVisualPaceOption(requestedPace)) return requestedPace;

  const existingPace = Number(existingValue);
  const keepsHistoricalValue =
    Number.isInteger(existingPace) &&
    existingPace >= 4 &&
    existingPace <= 10 &&
    requestedPace === existingPace;

  return keepsHistoricalValue ? requestedPace : null;
}

export type ScriptDurationEstimate = {
  seconds: number;
  unitCount: number;
  unitLabel: 'characters' | 'words';
  unitsPerMinute: number;
};

export type ScriptDurationTarget = {
  seconds: number;
  targetUnits: number;
  minUnits: number;
  maxUnits: number;
  unitLabel: 'characters' | 'words';
  unitsPerMinute: number;
  voiceRate: number;
};

export type ScriptDurationAlignment = {
  status: 'empty' | 'too_short' | 'within_range' | 'too_long';
  targetSeconds: number;
  minSeconds: number;
  maxSeconds: number;
  ratio: number;
  estimate: ScriptDurationEstimate;
};

export type FacelessVisualPlanningEstimate = {
  durationSeconds: number;
  estimatedImageCount: number;
  source: 'configured_duration' | 'manual_script_estimate';
  scriptEstimate: ScriptDurationEstimate | null;
  visualPaceSeconds: number;
};

export function estimateScriptDuration(
  text: unknown,
  language: unknown,
  voiceRate: unknown = 1,
): ScriptDurationEstimate {
  const normalizedText = String(text || '').trim();
  const languageCode = findSupportedContentLanguage(language)?.code || 'en';
  const usesCharacters = CHARACTER_BASED_LANGUAGES.has(languageCode);
  const unitCount = usesCharacters
    ? Array.from(normalizedText.replace(/[\s,.!?;:'"()[\]{}<>，。！？；：、…“”‘’—–\-_/\\|@#$%^&*+=~`]+/g, '')).length
    : normalizedText
      .split(/\s+/)
      .filter((token) => /[A-Za-zÀ-ÖØ-öø-ÿА-Яа-яЁё0-9]/.test(token))
      .length;
  const unitsPerMinute = SPEAKING_UNITS_PER_MINUTE[languageCode] || 180;
  const normalizedVoiceRate = Math.max(0.5, Math.min(2, Number(voiceRate) || 1));
  const seconds = unitCount > 0
    ? Math.max(1, Math.round((unitCount / unitsPerMinute) * 60 / normalizedVoiceRate))
    : 0;

  return {
    seconds,
    unitCount,
    unitLabel: usesCharacters ? 'characters' : 'words',
    unitsPerMinute,
  };
}

export function resolveScriptDurationTarget(
  durationSeconds: unknown,
  language: unknown,
  voiceRate: unknown = 1,
  tolerance = 0.1,
): ScriptDurationTarget {
  const seconds = Math.max(
    1,
    Math.min(600, Math.round(Number(durationSeconds) || 60)),
  );
  const languageCode = findSupportedContentLanguage(language)?.code || 'en';
  const usesCharacters = CHARACTER_BASED_LANGUAGES.has(languageCode);
  const unitsPerMinute = SPEAKING_UNITS_PER_MINUTE[languageCode] || 180;
  const normalizedVoiceRate = Math.max(
    0.5,
    Math.min(2, Number(voiceRate) || 1),
  );
  const normalizedTolerance = Math.max(
    0,
    Math.min(0.5, Number(tolerance) || 0),
  );
  const targetUnits = Math.max(
    1,
    Math.round(
      (seconds / 60) * unitsPerMinute * normalizedVoiceRate,
    ),
  );

  return {
    seconds,
    targetUnits,
    minUnits: Math.max(1, Math.round(targetUnits * (1 - normalizedTolerance))),
    maxUnits: Math.max(1, Math.round(targetUnits * (1 + normalizedTolerance))),
    unitLabel: usesCharacters ? 'characters' : 'words',
    unitsPerMinute,
    voiceRate: normalizedVoiceRate,
  };
}

export function assessScriptDurationAlignment(
  text: unknown,
  language: unknown,
  voiceRate: unknown,
  targetDurationSeconds: unknown,
  tolerance = 0.15,
): ScriptDurationAlignment {
  const target = resolveScriptDurationTarget(
    targetDurationSeconds,
    language,
    voiceRate,
    tolerance,
  );
  const estimate = estimateScriptDuration(text, language, voiceRate);
  const minSeconds = Math.max(
    1,
    Math.round(target.seconds * (1 - Math.max(0, Math.min(0.5, tolerance)))),
  );
  const maxSeconds = Math.max(
    minSeconds,
    Math.round(target.seconds * (1 + Math.max(0, Math.min(0.5, tolerance)))),
  );
  const ratio = estimate.seconds / target.seconds;

  let status: ScriptDurationAlignment['status'] = 'within_range';
  if (!estimate.unitCount) {
    status = 'empty';
  } else if (estimate.seconds < minSeconds) {
    status = 'too_short';
  } else if (estimate.seconds > maxSeconds) {
    status = 'too_long';
  }

  return {
    status,
    targetSeconds: target.seconds,
    minSeconds,
    maxSeconds,
    ratio,
    estimate,
  };
}

export function formatEstimatedDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes === 0) return `${remainingSeconds}s`;
  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m ${remainingSeconds}s`;
}

export function resolveFacelessVisualPlanningEstimate(input: {
  configuredDurationSeconds: unknown;
  isManualScript: boolean;
  language: unknown;
  scriptText: unknown;
  visualPaceSeconds: unknown;
  voiceRate?: unknown;
}): FacelessVisualPlanningEstimate {
  const configuredDuration = Math.max(
    1,
    Math.min(600, Math.round(Number(input.configuredDurationSeconds) || 60)),
  );
  const visualPaceSeconds = normalizeFacelessVisualPace(
    input.visualPaceSeconds,
  );
  const scriptEstimate = input.isManualScript
    ? estimateScriptDuration(input.scriptText, input.language, input.voiceRate)
    : null;
  const usesManualEstimate = Boolean(scriptEstimate?.unitCount);
  const durationSeconds = usesManualEstimate
    ? Math.max(1, Math.min(600, scriptEstimate!.seconds))
    : configuredDuration;

  return {
    durationSeconds,
    estimatedImageCount: Math.max(
      2,
      Math.ceil(durationSeconds / visualPaceSeconds) + 1,
    ),
    source: usesManualEstimate
      ? 'manual_script_estimate'
      : 'configured_duration',
    scriptEstimate,
    visualPaceSeconds,
  };
}
